import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
  } from "react";
  import CustomAlert from "../components/CustomAlert";
  
  /* ---------- context ---------- */
  const AlertContext = createContext({
    alert: /** @type{(msg:string)=>void} */ (() => {}),
    confirm: /** @type{(msg:string, opts?:{confirmLabel?:string,cancelLabel?:string})=>Promise<boolean>} */ (async () => false),
  });
  
  /* ---------- provider ---------- */
  export function AlertProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    msg: "",
    mode: "alert",             // "alert" | "confirm"
    resolve: null,             // for confirm result
    confirmLabel: "OK",
    cancelLabel: "Cancel",
  });
  
    const alert = useCallback((msg) => {
    setState((s) => ({
      ...s,
      open: true,
      mode: "alert",
      msg: String(msg),
      resolve: null,
      confirmLabel: "OK",
      cancelLabel: "Cancel",
    }));
    }, []);
  
    const close = () => {
    // if this was a confirm and user closed/cancelled
    if (typeof state.resolve === "function" && state.mode === "confirm") {
      state.resolve(false);
    }
    setState((s) => ({ ...s, open: false, resolve: null }));
  };

  const confirm = useCallback((msg, opts = {}) => {
    return new Promise((resolve) => {
      setState((s) => ({
        ...s,
        open: true,
        mode: "confirm",
        msg: String(msg),
        resolve,
        confirmLabel: opts.confirmLabel ?? "OK",
        cancelLabel: opts.cancelLabel ?? "Cancel",
      }));
    });
  }, []);
  
    /* hijack window.alert just once */
    useEffect(() => {
      const native = window.alert;      // remember the old one
      window.alert = alert;             // override
      return () => {
        window.alert = native;          // restore on unmount (tests, Storybook, etc.)
      };
    }, [alert]);
  
    return (
      <AlertContext.Provider value={{ alert, confirm }}>
        {children}
          {state.open && (
        <CustomAlert
          message={state.msg}
          mode={state.mode}
          onClose={close}
          onConfirm={() => {
            if (typeof state.resolve === "function") state.resolve(true);
            setState((s) => ({ ...s, open: false, resolve: null }));
          }}
          confirmLabel={state.confirmLabel}
          cancelLabel={state.cancelLabel}
        />
      )}
      </AlertContext.Provider>
    );
  }
  
  /* small helper so you can `const {alert} = useAlert()` if you want */
  export const useAlert = () => useContext(AlertContext);
  